import { useRef } from 'react';
import { ORDER_PAYMENT_STATUSES } from '../../services/exports/Constants';

interface ReturnParams {
  offsite?: string;
  redirect_status?: string;
}

/**
 * A customer back from their bank before it answered (402 on return) lands on the order screen
 * without redirect_status=succeeded, so the after-purchase path (basket cleared, purchase
 * tracked, the "order placed" toast) would never run once the payment settles. The returned
 * function is fed every payment status the screen learns and runs `onPaid` on the first paid
 * one, once per visit.
 */
export default function useOffsiteLateSuccess({ offsite, redirect_status }: ReturnParams, onPaid: () => void) {
  const awaitingRef = useRef(offsite === '1' && !redirect_status);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  // The return handler's own success navigates here with redirect_status=succeeded, which runs the
  // after-purchase path itself; the same screen instance must not run it a second time.
  if (redirect_status) {
    awaitingRef.current = false;
  }

  // Read through refs: the order screen binds its Pusher handlers once, on mount.
  const notePaymentStatus = useRef((status: ORDER_PAYMENT_STATUSES | null | undefined): boolean => {
    if (!awaitingRef.current || status !== ORDER_PAYMENT_STATUSES.PAID) {
      return false;
    }

    awaitingRef.current = false;
    onPaidRef.current();

    return true;
  });

  return notePaymentStatus.current;
}
